import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useMemo } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Select, { SelectOption } from '@/components/molecules/select/select';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './multiple-selector.module.scss';

export enum DisplayLabel {
  ListHeadline = 'listHeadline',
  ProductAllotmentDate = 'productAllotmentDate',
}

const DISPLAY_LABEL_TO_FIELD: Record<
  DisplayLabel,
  'listHeadline' | 'productAllotmentDate'
> = {
  [DisplayLabel.ListHeadline]: 'listHeadline',
  [DisplayLabel.ProductAllotmentDate]: 'productAllotmentDate',
} as const;

export interface MultipleSelectorProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether the extra has distinction for homebound and outbound travels */
  containsOutHomeProducts?: boolean;
  /** Indicates the direction of the homebound and outbound product */
  direction?: Content.Components['ProductTransportDirection'];
  /** Which product field to display as option label */
  displayLabel?: DisplayLabel;
  /** An event that is triggered on select change */
  onChange?: (selectedValue: string) => void;
  /** The passenger data */
  passenger: Content.Components['GenericProductPassengerDto'];
}

const MultipleSelector = ({
  className,
  containsOutHomeProducts = false,
  direction = 'default',
  displayLabel = DisplayLabel.ListHeadline,
  onChange,
  passenger,
}: MultipleSelectorProps) => {
  const { t } = useI18n();
  const currencyFormatter = useCurrencyFormatter();

  const relevantProducts = passenger.relevantPassengerBookingProducts;

  const filteredRelevantProducts = useMemo(() => {
    return relevantProducts.filter(product =>
      containsOutHomeProducts
        ? product.direction === direction
        : product.direction === 'default',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantProducts]);

  const { firstName, lastName, placeholders } = passenger.passenger;

  const headline =
    !firstName && !lastName
      ? `${placeholders.firstName} ${placeholders.lastName}`
      : `${firstName} ${lastName}`;

  const selectedProduct =
    filteredRelevantProducts.find(product => product.isSelected) ?? null;

  const selectedOption = selectedProduct?.bindId ?? 'none';

  const price =
    selectedProduct?.price ??
    (filteredRelevantProducts.length > 0
      ? Math.min(...filteredRelevantProducts.map(product => product.price))
      : null);

  const localOptions: SelectOption[] = useMemo(
    () => {
      const labelField = DISPLAY_LABEL_TO_FIELD[displayLabel];

      return [
        {
          name: t('items.modules.bookingFlow.noneSelected'),
          value: 'none',
        },
        ...filteredRelevantProducts.map(product => ({
          id: product.bindId,
          name: product[labelField],
          selected: product.isSelected,
          value: product.bindId,
        })),
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRelevantProducts, displayLabel],
  );

  const hasSelectedProduct = localOptions.some(
    option => option.selected && option.value !== 'none',
  );

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isDisabled]: passenger.isDisabled,
      })}
    >
      <Select
        label={t('items.modules.bookingFlow.select')}
        onChange={event => onChange?.(event.target.value)}
        options={localOptions}
        value={selectedOption}
      />

      <div className={styles.content}>
        {headline && (
          <div className={styles.headline}>
            <Typography tag="h3" tagStyle="bodyMediumBold">
              {headline}
            </Typography>
          </div>
        )}

        {!passenger.isDisabled && typeof price === 'number' && (
          <div className={styles.price}>
            <Typography tagStyle="bodySmall">
              {!hasSelectedProduct &&
                price > 0 &&
                `${t('items.common.words.from')} `}

              {price > 0 && currencyFormatter(price)}
            </Typography>
          </div>
        )}

        {passenger.isDisabled && passenger.disabledText && (
          <div className={styles.price}>
            <Typography tagStyle="bodySmall">
              {passenger.disabledText}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

MultipleSelector.displayName = 'MultipleSelector';

export default MultipleSelector;
